/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
  InternalLinkRefQueryResult,
  TestimonialRefQueryResult,
  PersonalizedSectionBuilderRefQueryResult,
  CTABannerRefQueryResult,
  PersonRefQueryResult,
  LogoBandRefQueryResult,
  PlanRefQueryResult,
  ReferenceCardSelectedPagRefQueryResult,
  ImageRefQueryResult,
} from '~/sanity/types';
import {
  internalLinkRefName,
  internalLinkRefProjection,
} from '~/queries/references/internalLinkRef.query';
import {
  personalizedSectionBuilderRefName,
  personalizedSectionBuilderRefProjection,
} from '~/queries/references/personalizedSectionBuilderRef.query';
import {
  testimonialRefName,
  testimonialRefProjection,
} from '~/queries/references/testimonialRef.query';
import ExtractedRef from '~/utils/ExtractedRef';
import { isValidRefType, sanityRefTypes } from '~/utils/SanityRefsUtil';
import { CTABannerRefName, CTABannerRefProjection } from '~/queries/references/CTABannerRef.query';
import { personRefName, personRefProjection } from '~/queries/references/personRef.query';
import { logoBandRefName, logoBandRefProjection } from '~/queries/references/logoBandRef.query';
import { planRefName, planRefProjection } from '~/queries/references/planRef.query';
import {
  referenceCardSelectedPagRefName,
  referenceCardSelectedPagRefProjection,
} from '~/queries/references/referenceCardSelectedPagRef.query';
import type { NuxtApp } from '#app';
import { imageRefName, imageRefProjection } from '~/queries/references/imageRef.query';

const resolvedRefs = new Map<string, any>();

const getResolvedRefPromise = async <T>({
  _id,
  _type,
}: {
  _id?: string;
  _type: SanityRefTypes;
}) => {
  const resolvedRef = ref<T | null>(null);
  if (!_id) {
    return resolvedRef;
  }
  const key = ExtractedRef.getKeyFromTypeAndId({ _type, _id });
  const { data } = await useAsyncData<T>(key, async () => {
    const ref = resolvedRefs.get(key);
    if (!ref) {
      throw new Error(`Ref with key: ${key} not found`);
    }
    return ref as T;
  });
  resolvedRef.value = data.value;
  return resolvedRef;
};

export const useRefsStore = () => {
  const addResolvedRef = (ref: ExtractedRef, value: any) => {
    const key = ref.getKey();
    if (!value || !key || resolvedRefs.get(key)) {
      return;
    }
    resolvedRefs.set(key, value);
  };

  const getResolvedInternalLinkRef = (key?: string) => {
    return getResolvedRefPromise<InternalLinkRefQueryResult>({
      _id: key,
      _type: internalLinkRefName,
    });
  };

  const getResolvedPlan = (key: string) => {
    return getResolvedRefPromise<PlanRefQueryResult>({
      _id: key,
      _type: planRefName,
    });
  };

  const getResolvedCTABanner = (key?: string) => {
    return getResolvedRefPromise<CTABannerRefQueryResult>({
      _id: key,
      _type: CTABannerRefName,
    });
  };

  const getResolvedLogoBand = (key?: string) => {
    return getResolvedRefPromise<LogoBandRefQueryResult>({
      _id: key,
      _type: logoBandRefName,
    });
  };
  const getResolvedImage = (key?: string) => {
    return getResolvedRefPromise<ImageRefQueryResult>({
      _id: key,
      _type: imageRefName,
    });
  };

  const getResolvedPerson = (key?: string) => {
    return getResolvedRefPromise<PersonRefQueryResult>({
      _id: key,
      _type: personRefName,
    });
  };

  const getResolvedTestimonial = (key?: string) => {
    return getResolvedRefPromise<TestimonialRefQueryResult>({
      _id: key,
      _type: testimonialRefName,
    });
  };

  const getResolvedReferenceCard = (key?: string) => {
    return getResolvedRefPromise<ReferenceCardSelectedPagRefQueryResult>({
      _id: key,
      _type: referenceCardSelectedPagRefName,
    });
  };

  const getResolvedPersonalizedSectionBuilder = (key?: string) => {
    return getResolvedRefPromise<PersonalizedSectionBuilderRefQueryResult>({
      _id: key,
      _type: personalizedSectionBuilderRefName,
    });
  };

  const getSanityQueryAndParamsFromRefs = (refs: ExtractedRef[]) => {
    const query = groq`
    {
      "${internalLinkRefName}":  *[_id in $internalLinksRefs]
        {
          "": *[_id == ^._id][0] ${internalLinkRefProjection}
        },
      "${personalizedSectionBuilderRefName}": *[_id in $personalizedSectionBuilderRefs]
        {
          "": *[_id == ^._id][0] ${personalizedSectionBuilderRefProjection}
        },
      "${testimonialRefName}": *[_id in $testimonialRefs]
        {
          "": *[_id == ^._id][0] ${testimonialRefProjection}
        },
        "${CTABannerRefName}": *[_id in $CTABannerRefs]
        {
          "": *[_id == ^._id][0] ${CTABannerRefProjection}
        },
        "${personRefName}": *[_id in $personRefs]
        {
          "": *[_id == ^._id][0] ${personRefProjection}
        },
        "${logoBandRefName}": *[_id in $logoBandRefs]
        {
          "": *[_id == ^._id][0] ${logoBandRefProjection}
        },
        "${imageRefName}": *[_id in $imageRefs]
        {
          "": *[_id == ^._id][0] ${imageRefProjection}
        },
        "${planRefName}": *[_id in $planRefs]
        {
          "": *[_id == ^._id][0] ${planRefProjection}
        },
        "${referenceCardSelectedPagRefName}": *[_id in $referenceCardSelectedPagRefs]
        {
          "": *[_id == ^._id][0] ${referenceCardSelectedPagRefProjection}
        },
    }
    `;

    const params = {
      internalLinksRefs: refs
        .filter((ref) => ref.getType() === internalLinkRefName)
        .map((ref) => ref.getId()),
      personalizedSectionBuilderRefs: refs
        .filter((ref) => ref.getType() === personalizedSectionBuilderRefName)
        .map((ref) => ref.getId()),
      testimonialRefs: refs
        .filter((ref) => ref.getType() === testimonialRefName)
        .map((ref) => ref.getId()),
      CTABannerRefs: refs
        .filter((ref) => ref.getType() === CTABannerRefName)
        .map((ref) => ref.getId()),
      personRefs: refs.filter((ref) => ref.getType() === personRefName).map((ref) => ref.getId()),
      imageRefs: refs.filter((ref) => ref.getType() === imageRefName).map((ref) => ref.getId()),
      logoBandRefs: refs
        .filter((ref) => ref.getType() === logoBandRefName)
        .map((ref) => ref.getId()),
      planRefs: refs.filter((ref) => ref.getType() === planRefName).map((ref) => ref.getId()),
      referenceCardSelectedPagRefs: refs
        .filter((ref) => ref.getType() === referenceCardSelectedPagRefName)
        .map((ref) => ref.getId()),
    };

    return { query, params };
  };

  const storeRefs = ({ refs, data }: { refs: ExtractedRef[]; data: any }) => {
    for (const ref of refs) {
      sanityRefTypes.forEach((type) => {
        const resolvedRef = (data as any)[type]?.find((item: any) => item._id === ref.getId());
        if (!resolvedRef) {
          return;
        }
        addResolvedRef(ref, resolvedRef);
      });
    }
  };
  const fetchSanityData = async (refs: ExtractedRef[]) => {
    const query = groq`
    {
      "${internalLinkRefName}":  *[_id in $internalLinksRefs]
        {
          "": *[_id == ^._id][0] ${internalLinkRefProjection}
        },
      "${personalizedSectionBuilderRefName}": *[_id in $personalizedSectionBuilderRefs]
        {
          "": *[_id == ^._id][0] ${personalizedSectionBuilderRefProjection}
        },
      "${testimonialRefName}": *[_id in $testimonialRefs]
        {
          "": *[_id == ^._id][0] ${testimonialRefProjection}
        },
        "${CTABannerRefName}": *[_id in $CTABannerRefs]
        {
          "": *[_id == ^._id][0] ${CTABannerRefProjection}
        },
        "${personRefName}": *[_id in $personRefs]
        {
          "": *[_id == ^._id][0] ${personRefProjection}
        },
        "${logoBandRefName}": *[_id in $logoBandRefs]
        {
          "": *[_id == ^._id][0] ${logoBandRefProjection}
        },
        "${imageRefName}": *[_id in $imageRefs]
        {
          "": *[_id == ^._id][0] ${imageRefProjection}
        },
        "${planRefName}": *[_id in $planRefs]
        {
          "": *[_id == ^._id][0] ${planRefProjection}
        },
        "${referenceCardSelectedPagRefName}": *[_id in $referenceCardSelectedPagRefs]
        {
          "": *[_id == ^._id][0] ${referenceCardSelectedPagRefProjection}
        },
    }
    `;
    const sanity = useSanity();
    const data = await sanity.fetch(query, {
      internalLinksRefs: refs
        .filter((ref) => ref.getType() === internalLinkRefName)
        .map((ref) => ref.getId()),
      personalizedSectionBuilderRefs: refs
        .filter((ref) => ref.getType() === personalizedSectionBuilderRefName)
        .map((ref) => ref.getId()),
      testimonialRefs: refs
        .filter((ref) => ref.getType() === testimonialRefName)
        .map((ref) => ref.getId()),
      CTABannerRefs: refs
        .filter((ref) => ref.getType() === CTABannerRefName)
        .map((ref) => ref.getId()),
      personRefs: refs.filter((ref) => ref.getType() === personRefName).map((ref) => ref.getId()),
      imageRefs: refs.filter((ref) => ref.getType() === imageRefName).map((ref) => ref.getId()),
      logoBandRefs: refs
        .filter((ref) => ref.getType() === logoBandRefName)
        .map((ref) => ref.getId()),
      planRefs: refs.filter((ref) => ref.getType() === planRefName).map((ref) => ref.getId()),
      referenceCardSelectedPagRefs: refs
        .filter((ref) => ref.getType() === referenceCardSelectedPagRefName)
        .map((ref) => ref.getId()),
    });
    if (!data) {
      return;
    }
    for (const ref of refs) {
      sanityRefTypes.forEach((type) => {
        const resolvedRef = (data as any)[type]?.find((item: any) => item._id === ref.getId());
        if (!resolvedRef) {
          return;
        }
        addResolvedRef(ref, resolvedRef);
      });
    }
    return data;
  };

  const extractRefs = (doc: any, parentType: string | null = null): ExtractedRef[] => {
    const refs: ExtractedRef[] = [];

    const findRefs = (doc: any, parentType: string | null) => {
      if (!doc || (typeof doc !== 'object' && !Array.isArray(doc))) {
        return;
      }

      if (doc._type && doc._type !== 'reference') {
        parentType = doc._type;
      }

      if (Array.isArray(doc)) {
        doc.forEach((item) => findRefs(item, parentType));
        return;
      }

      if (Object.prototype.hasOwnProperty.call(doc, '_ref')) {
        if (!parentType) {
          return;
        }
        if (!isValidRefType(parentType)) {
          return;
        }
        const ref = new ExtractedRef({
          _type: parentType,
          _id: doc._ref,
        });

        if (!resolvedRefs.has(ref.getKey()) && !refs.some((ref) => ref.getId() === doc._ref)) {
          refs.push(ref);
        }
      }

      Object.values(doc).forEach((child) => findRefs(child, parentType));
    };

    findRefs(doc, parentType);
    return refs;
  };

  const resolveRefs = async (doc: any, nuxtApp: NuxtApp) => {
    const refs = extractRefs(doc);
    const resp = await nuxtApp.runWithContext(async () => await fetchSanityData(refs));
    const refs2 = extractRefs(resp);
    await nuxtApp.runWithContext(async () => await fetchSanityData(refs2));
  };

  return {
    getResolvedInternalLinkRef,
    getResolvedPlan,
    getResolvedCTABanner,
    getResolvedLogoBand,
    getResolvedPerson,
    getResolvedTestimonial,
    getResolvedReferenceCard,
    getResolvedImage,
    getResolvedPersonalizedSectionBuilder,
    resolveRefs,
    extractRefs,
    getSanityQueryAndParamsFromRefs,
    storeRefs,
  };
};
